import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import api, { fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs font-semibold tracking-wider uppercase text-slate-400">{label}</div>
    <div className="text-sm text-slate-800 mt-1">{value || "—"}</div>
  </div>
);

export default function HrisDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/employees/${id}`).then(({ data }) => setData(data)).catch(() => {}); }, [id]);
  if (!data) return <div className="text-slate-400 animate-pulse">Loading employee…</div>;
  const e = data.employee;

  return (
    <div className="uemp-fade-in">
      <Button variant="ghost" onClick={() => navigate("/hris")} className="mb-3 -ml-2 text-slate-500" data-testid="back-to-hris">
        <Icons.ArrowLeft className="h-4 w-4 mr-1.5" /> Back to directory
      </Button>
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-5 flex flex-col sm:flex-row sm:items-center gap-5">
        {e.photo_url ? (
          <img src={e.photo_url} alt={`${e.first_name} ${e.last_name}`} className="h-20 w-20 rounded-full object-cover shrink-0" data-testid="emp-detail-avatar" />
        ) : (
          <div className="h-20 w-20 rounded-full bg-blue-600 text-white flex items-center justify-center text-2xl font-semibold shrink-0" data-testid="emp-detail-avatar">
            {e.first_name[0]}{e.last_name[0]}
          </div>
        )}
        <div className="flex-1">
          <div className="text-xl font-semibold text-slate-900">{e.first_name} {e.middle_name} {e.last_name}</div>
          <div className="text-slate-500 text-sm">{e.position} · {e.department}</div>
          <div className="flex flex-wrap gap-2 mt-2">
            <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">{e.employment_status}</Badge>
            <Badge variant="secondary">{e.employment_type}</Badge>
            <Badge variant="outline" className="font-mono text-xs">{e.employee_no}</Badge>
          </div>
        </div>
        {data.manager && (
          <div className="text-right">
            <div className="text-xs font-semibold tracking-wider uppercase text-slate-400">Reports To</div>
            <div className="text-sm text-slate-800 mt-1">{data.manager.first_name} {data.manager.last_name}</div>
            <div className="text-xs text-slate-500">{data.manager.position}</div>
          </div>
        )}
      </div>

      <Tabs defaultValue="personal">
        <TabsList>
          <TabsTrigger value="personal">Personal</TabsTrigger>
          <TabsTrigger value="employment">Employment</TabsTrigger>
          <TabsTrigger value="contact">Contact</TabsTrigger>
        </TabsList>
        <TabsContent value="personal">
          <div className="bg-white rounded-xl border border-slate-200 p-6 grid sm:grid-cols-3 gap-5">
            <Field label="Preferred Name" value={e.preferred_name} />
            <Field label="Birth Date" value={fmtDate(e.birth_date)} />
            <Field label="Gender" value={e.gender} />
            <Field label="Civil Status" value={e.civil_status} />
            <Field label="Biometric ID" value={e.biometric_id} />
            <Field label="Job Level" value={e.job_level} />
          </div>
        </TabsContent>
        <TabsContent value="employment">
          <div className="bg-white rounded-xl border border-slate-200 p-6 grid sm:grid-cols-3 gap-5">
            <Field label="Date Hired" value={fmtDate(e.date_hired)} />
            <Field label="Date Regularized" value={fmtDate(e.date_regularized)} />
            <Field label="Cost Center" value={e.cost_center} />
            <Field label="Work Location" value={e.work_location} />
            <Field label="Department" value={e.department} />
            <Field label="Position" value={e.position} />
          </div>
        </TabsContent>
        <TabsContent value="contact">
          <div className="bg-white rounded-xl border border-slate-200 p-6 grid sm:grid-cols-3 gap-5">
            <Field label="Email" value={e.email} />
            <Field label="Mobile" value={e.mobile} />
            <Field label="Address" value={e.address} />
            <Field label="Emergency Contact" value={e.emergency_contact?.name} />
            <Field label="Relation" value={e.emergency_contact?.relation} />
            <Field label="Emergency Mobile" value={e.emergency_contact?.mobile} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
